import React, { useState, useEffect, useRef, useCallback } from 'react';

interface Schedule {
  id: number;
  project_id: number;
  schedule_type: string;
  custom_name: string | null;
  extraction_mode: string;
  header_row: number;
  room_column: number;
  extraction_status: string;
  extraction_error: string | null;
  version_count: number;
  current_version: {
    version_number: number;
    total_rows: number;
    matched_count: number;
    unmatched_count: number;
    extracted_data: Array<Record<string, string | number>> | null;
    created_at: string;
  } | null;
  created_at: string;
  updated_at: string;
}

interface Room {
  id: number;
  number: string;
  name: string | null;
}

interface MappingUpdate {
  table_index: number;
  row_index: number;
  raw_value: string;
  room_ids: number[];
}

interface Props {
  schedule: Schedule;
  token: string;
  onMappingsSaved: () => void;
}

type FilterType = 'all' | 'unmatched' | 'matched';

export function ScheduleRoomMapping({ schedule, token, onMappingsSaved }: Props) {
  const [versionDetail, setVersionDetail] = useState<any>(null);
  const [projectRooms, setProjectRooms] = useState<Room[]>([]);
  const [pendingMappings, setPendingMappings] = useState<Map<string, MappingUpdate>>(new Map());
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [roomSearches, setRoomSearches] = useState<Map<string, string>>(new Map());
  const [activeDropdowns, setActiveDropdowns] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const dropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    loadVersionDetail();
    loadProjectRooms();
  }, [schedule.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const clickedInside = Array.from(dropdownRefs.current.values()).some(
        ref => ref && ref.contains(e.target as Node)
      );
      if (!clickedInside) {
        setActiveDropdowns(new Set());
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadVersionDetail = async () => {
    try {
      const response = await fetch(`/api/schedules/${schedule.id}/versions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const versions = await response.json();
        if (versions && versions.length > 0) {
          setVersionDetail(versions[0]);
        }
      }
    } catch {
      // Silently fail
    }
  };

  const loadProjectRooms = async () => {
    try {
      const response = await fetch(`/api/projects/${schedule.project_id}/rooms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const rooms = await response.json();
        setProjectRooms(rooms);
      }
    } catch {
      // Silently fail
    }
  };

  const formatRoomDisplay = (room: Room) => {
    return room.name ? `${room.number} - ${room.name}` : room.number;
  };

  const getRowKey = (tableIndex: number, rowIndex: number) => {
    return `${tableIndex}-${rowIndex}`;
  };

  const getMatchedRoomsForRow = (tableIndex: number, rowIndex: number) => {
    if (!versionDetail?.matched_rooms) return [];
    
    const matchedRooms = versionDetail.matched_rooms.matched_rooms || [];
    const rooms: Array<{ room_id: number; room_number: string; match_type: string }> = [];
    
    for (const match of matchedRooms) {
      const rowIndices = match.row_indices || [];
      const isMatched = rowIndices.some(
        (ref: any) => ref.table_index === tableIndex && ref.row_index === rowIndex
      );
      if (isMatched) {
        rooms.push({
          room_id: match.room_id,
          room_number: match.room_number,
          match_type: match.match_type,
        });
      }
    }
    
    return rooms;
  };

  const isRowUnmatched = (tableIndex: number, rowIndex: number) => {
    if (!versionDetail?.matched_rooms) return false;
    
    const unmatchedRows = versionDetail.matched_rooms.unmatched_rows || [];
    return unmatchedRows.some(
      (r: any) => r.table_index === tableIndex && r.row_index === rowIndex
    );
  };

  const getExtractedValue = (tableIndex: number, rowIndex: number) => {
    if (!versionDetail?.extracted_data) return '';
    
    const tables = versionDetail.extracted_data.tables || [];
    if (tableIndex < tables.length) {
      const table = tables[tableIndex];
      const rows = table.rows || [];
      if (rowIndex < rows.length) {
        const row = rows[rowIndex];
        const cells = row.cells || [];
        const roomCol = schedule.room_column;
        if (roomCol < cells.length) {
          return String(cells[roomCol]);
        }
      }
    }
    return '';
  };

  const getAllRows = () => {
    if (!versionDetail?.extracted_data) return [];
    
    const tables = versionDetail.extracted_data.tables || [];
    const rows: Array<{ table_index: number; row_index: number }> = [];
    
    tables.forEach((table: any, tableIdx: number) => {
      const tableRows = table.rows || [];
      tableRows.forEach((_: any, rowIdx: number) => {
        rows.push({ table_index: tableIdx, row_index: rowIdx });
      });
    });
    
    return rows;
  };

  const getFilteredRows = () => {
    let rows = getAllRows();
    
    if (filter === 'unmatched') {
      rows = rows.filter(r => isRowUnmatched(r.table_index, r.row_index));
    } else if (filter === 'matched') {
      rows = rows.filter(r => !isRowUnmatched(r.table_index, r.row_index));
    }
    
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      rows = rows.filter(r => {
        const extractedValue = getExtractedValue(r.table_index, r.row_index);
        const matchedRooms = getMatchedRoomsForRow(r.table_index, r.row_index);
        const roomNumbers = matchedRooms.map(rm => rm.room_number).join(' ');
        
        return (
          extractedValue.toLowerCase().includes(searchLower) ||
          roomNumbers.toLowerCase().includes(searchLower)
        );
      });
    }
    
    return rows;
  };

  const handleRoomSelect = (tableIndex: number, rowIndex: number, room: Room) => {
    const key = getRowKey(tableIndex, rowIndex);
    
    setPendingMappings(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(key);
      
      if (existing) {
        if (!existing.room_ids.includes(room.id)) {
          newMap.set(key, { ...existing, room_ids: [...existing.room_ids, room.id] });
        }
      } else {
        newMap.set(key, {
          table_index: tableIndex,
          row_index: rowIndex,
          raw_value: getExtractedValue(tableIndex, rowIndex),
          room_ids: [room.id],
        });
      }
      
      return newMap;
    });
    
    setActiveDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
    
    setRoomSearches(prev => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  };

  const handleRemoveRoom = (tableIndex: number, rowIndex: number, roomId: number) => {
    const key = getRowKey(tableIndex, rowIndex);
    
    setPendingMappings(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(key);
      
      if (existing) {
        const newRoomIds = existing.room_ids.filter(id => id !== roomId);
        if (newRoomIds.length === 0) {
          newMap.delete(key);
        } else {
          newMap.set(key, { ...existing, room_ids: newRoomIds });
        }
      }
      
      return newMap;
    });
  };

  const toggleDropdown = (key: string) => {
    setActiveDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getFilteredRooms = (key: string) => {
    const searchTerm = roomSearches.get(key) || '';
    if (!searchTerm) return projectRooms;
    
    const searchLower = searchTerm.toLowerCase();
    return projectRooms.filter(room => {
      return (
        room.number.toLowerCase().includes(searchLower) ||
        (room.name && room.name.toLowerCase().includes(searchLower))
      );
    });
  };

  const handleSaveMappings = async () => {
    if (pendingMappings.size === 0) {
      setError('No changes to save');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const mappings = Array.from(pendingMappings.values());
      
      const response = await fetch(`/api/schedules/${schedule.id}/room-mappings`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mappings }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save mappings');
      }
      
      const result = await response.json();
      setSuccess(`Successfully saved ${mappings.length} mapping(s)`);
      setPendingMappings(new Map());
      
      await loadVersionDetail();
      onMappingsSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`/api/schedules/${schedule.id}/rematch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to rematch');
      }
      
      const result = await response.json();
      setSuccess(`Rematching complete: ${result.matched_count} matched, ${result.unmatched_count} unmatched`);
      
      await loadVersionDetail();
      onMappingsSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rematch');
    } finally {
      setRematching(false);
    }
  };

  const matchedCount = versionDetail?.matched_count || 0;
  const unmatchedCount = versionDetail?.unmatched_count || 0;
  const filteredRows = getFilteredRows();

  if (!versionDetail) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading room mappings...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Room Mappings</h3>
          <div style={{ display: 'flex', gap: '12px', fontSize: '13px' }}>
            <span>
              <span style={{ color: '#28a745', fontWeight: 'bold' }}>{matchedCount}</span>
              <span style={{ color: '#666' }}> matched</span>
            </span>
            <span>
              <span style={{ color: '#ffc107', fontWeight: 'bold' }}>{unmatchedCount}</span>
              <span style={{ color: '#666' }}> unmatched</span>
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleRematch}
            disabled={rematching}
            style={{
              padding: '6px 12px',
              background: rematching ? '#ccc' : '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: rematching ? 'not-allowed' : 'pointer',
              fontSize: '13px',
            }}
          >
            {rematching ? 'Rematching...' : 'Re-run Auto-Match'}
          </button>
          <button
            onClick={handleSaveMappings}
            disabled={saving || pendingMappings.size === 0}
            style={{
              padding: '6px 16px',
              background: saving || pendingMappings.size === 0 ? '#ccc' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving || pendingMappings.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            {saving ? 'Saving...' : `Save All Mappings (${pendingMappings.size})`}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 12px',
            background: '#fee',
            color: '#c00',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '10px 12px',
            background: '#e8f5e9',
            color: '#28a745',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['all', 'unmatched', 'matched'] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 12px',
                background: filter === f ? '#007bff' : '#f0f0f0',
                color: filter === f ? '#fff' : '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rooms or values..."
          style={{
            flex: 1,
            padding: '6px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px',
          }}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            border: '1px solid #ddd',
          }}
        >
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th
                style={{
                  padding: '10px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '60px',
                }}
              >
                Row
              </th>
              <th
                style={{
                  padding: '10px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                }}
              >
                Extracted Value
              </th>
              <th
                style={{
                  padding: '10px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '200px',
                }}
              >
                Status
              </th>
              <th
                style={{
                  padding: '10px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '250px',
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  No rows match the current filter
                </td>
              </tr>
            ) : (
              filteredRows.map(({ table_index, row_index }) => {
                const key = getRowKey(table_index, row_index);
                const extractedValue = getExtractedValue(table_index, row_index);
                const matchedRooms = getMatchedRoomsForRow(table_index, row_index);
                const unmatched = isRowUnmatched(table_index, row_index);
                const pendingMapping = pendingMappings.get(key);
                const isDropdownActive = activeDropdowns.has(key);
                const filteredRooms = getFilteredRooms(key);
                const roomSearch = roomSearches.get(key) || '';

                return (
                  <tr
                    key={key}
                    style={{
                      borderBottom: '1px solid #eee',
                      background: unmatched ? '#fff8e1' : '#fff',
                    }}
                  >
                    <td style={{ padding: '8px 10px', color: '#666' }}>
                      {row_index + 1}
                    </td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>
                      {extractedValue || '(empty)'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {unmatched ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            background: '#fff3e0',
                            color: '#f57c00',
                          }}
                        >
                          ⚠ Unmatched
                        </span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {matchedRooms.map((room) => (
                            <span
                              key={room.room_id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background:
                                  room.match_type === 'manual' ? '#e3f2fd' : '#e8f5e9',
                                color: room.match_type === 'manual' ? '#1976d2' : '#28a745',
                              }}
                            >
                              ✓ {room.room_number}
                              {room.match_type === 'manual' && (
                                <span style={{ fontSize: '10px' }}>(manual)</span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pendingMapping && pendingMapping.room_ids.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {pendingMapping.room_ids.map((roomId) => {
                              const room = projectRooms.find((r) => r.id === roomId);
                              if (!room) return null;
                              return (
                                <span
                                  key={roomId}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    background: '#e3f2fd',
                                    color: '#1976d2',
                                  }}
                                >
                                  {room.number}
                                  <button
                                    onClick={() => handleRemoveRoom(table_index, row_index, roomId)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '0',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      lineHeight: '1',
                                      color: '#1976d2',
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}

                        <div ref={(el) => { if (el) dropdownRefs.current.set(key, el); }}>
                          <button
                            onClick={() => toggleDropdown(key)}
                            style={{
                              padding: '4px 8px',
                              background: '#f0f0f0',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              width: '100%',
                              textAlign: 'left',
                            }}
                          >
                            {isDropdownActive ? 'Close' : '+ Add Room'}
                          </button>
                          {isDropdownActive && (
                            <div
                              style={{
                                position: 'absolute',
                                background: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                maxHeight: '200px',
                                overflow: 'auto',
                                zIndex: 1001,
                                marginTop: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                width: '240px',
                              }}
                            >
                              <input
                                type="text"
                                value={roomSearch}
                                onChange={(e) =>
                                  setRoomSearches((prev) => {
                                    const newMap = new Map(prev);
                                    newMap.set(key, e.target.value);
                                    return newMap;
                                  })
                                }
                                placeholder="Search rooms..."
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: 'none',
                                  borderBottom: '1px solid #eee',
                                  fontSize: '13px',
                                  boxSizing: 'border-box',
                                }}
                              />
                              {filteredRooms.length === 0 ? (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '13px',
                                  }}
                                >
                                  No rooms found
                                </div>
                              ) : (
                                filteredRooms.map((room) => (
                                  <div
                                    key={room.id}
                                    onClick={() =>
                                      handleRoomSelect(table_index, row_index, room)
                                    }
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #eee',
                                      fontSize: '13px',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#f5f5f5';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#fff';
                                    }}
                                  >
                                    {formatRoomDisplay(room)}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
